import {
  Box,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  HStack,
  Input,
  Radio,
  RadioGroup,
  Select,
  Text,
} from "@chakra-ui/react";
import React from "react";
import { phoneFormatter } from "../../../shared/utils/phone-formatter";
import { entityProfileModalBorders, entityProfileModalColors } from "../entityProfileModal.theme";
import EntityModalButton from "./EntityModalButton";

type EntityProfileContactCardShellProps = {
  children: React.ReactNode;
  onRemove?: () => void;
  removeLabel?: string;
  title: string;
  variant?: "readonly" | "editing";
  isRemoveDisabled?: boolean;
};

export function EntityProfileContactCardShell(props: EntityProfileContactCardShellProps) {
  const variant = props.variant ?? "readonly";

  return (
    <Box
      border={
        variant === "editing" ? entityProfileModalBorders.soft : entityProfileModalBorders.panel
      }
      borderRadius="2px"
      p={3}
    >
      <HStack justifyContent="space-between" mb={3}>
        <Text color={entityProfileModalColors.text} fontSize="14px" fontWeight={700}>
          {props.title}
        </Text>
        {props.onRemove ? (
          <EntityModalButton
            isIconOnly
            aria-label={props.removeLabel ?? "Remove contact"}
            disabled={props.isRemoveDisabled}
            iconClassName="fa fa-trash"
            kind="red"
            size="sm"
            title={props.removeLabel ?? "Remove contact"}
            variant="outline"
            onClick={props.onRemove}
          />
        ) : null}
      </HStack>
      {props.children}
    </Box>
  );
}

type EntityProfileContactGridProps = {
  children: React.ReactNode;
  columns?: 2 | 3;
};

export function EntityProfileContactGrid(props: EntityProfileContactGridProps) {
  const templateColumns =
    props.columns === 3
      ? {
          base: "1fr",
          md: "repeat(2, minmax(0, 1fr))",
          xl: "repeat(3, minmax(0, 1fr))",
        }
      : { base: "1fr", md: "repeat(2, minmax(0, 1fr))" };

  return (
    <Grid gap={3} templateColumns={templateColumns}>
      {props.children}
    </Grid>
  );
}

type EntityProfileContactDetail = {
  label: string;
  value: React.ReactNode;
};

type EntityProfileContactDetailsCardProps = {
  columns?: 2 | 3;
  fields: EntityProfileContactDetail[];
  title: string;
};

export function EntityProfileContactDetailsCard(props: EntityProfileContactDetailsCardProps) {
  return (
    <EntityProfileContactCardShell title={props.title}>
      <EntityProfileContactGrid columns={props.columns}>
        {props.fields.map((field) => (
          <Box key={field.label}>
            <Text
              color={entityProfileModalColors.labelText}
              fontSize="12px"
              fontWeight={700}
              letterSpacing="0.02em"
              textTransform="uppercase"
            >
              {field.label}
            </Text>
            <Text
              color={entityProfileModalColors.text}
              fontSize="14px"
              lineHeight="1.35"
              minH="19px"
            >
              {field.value}
            </Text>
          </Box>
        ))}
      </EntityProfileContactGrid>
    </EntityProfileContactCardShell>
  );
}

type EntityProfileContactInputFieldProps = {
  errorMessage?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  isDisabled: boolean;
  isInvalid?: boolean;
  isRequired?: boolean;
  label: string;
  maxLength?: number;
  type?: React.HTMLInputTypeAttribute;
  value: string;
  onChange: (value: string) => void;
};

export function EntityProfileContactInputField(props: EntityProfileContactInputFieldProps) {
  return (
    <FormControl isInvalid={props.isInvalid} isRequired={props.isRequired}>
      <FormLabel fontSize="13px" fontWeight={700}>
        {props.label}
      </FormLabel>
      <Input
        inputMode={props.inputMode}
        isDisabled={props.isDisabled}
        maxLength={props.maxLength}
        type={props.type}
        value={props.value}
        onChange={(event) => props.onChange(event.target.value)}
      />
      {props.errorMessage ? <FormErrorMessage>{props.errorMessage}</FormErrorMessage> : null}
    </FormControl>
  );
}

type EntityProfileContactPhoneFieldProps = {
  errorMessage?: string;
  isDisabled: boolean;
  isRequired?: boolean;
  label: string;
  showErrors: boolean;
  value: string;
  onChange: (value: string) => void;
};

export function EntityProfileContactPhoneField(props: EntityProfileContactPhoneFieldProps) {
  const isBlank = props.value.trim() === "";
  const isInvalid =
    props.showErrors &&
    ((props.isRequired === true && isBlank) ||
      (!isBlank && !phoneFormatter.isValidNumber(props.value)));

  return (
    <EntityProfileContactInputField
      errorMessage={props.errorMessage ?? "Enter a valid phone number."}
      inputMode="tel"
      isDisabled={props.isDisabled}
      isInvalid={isInvalid}
      isRequired={props.isRequired}
      label={props.label}
      maxLength={14}
      type="tel"
      value={props.value}
      onChange={props.onChange}
    />
  );
}

type EntityProfileContactSelectFieldProps = {
  errorMessage?: string;
  isDisabled: boolean;
  isInvalid?: boolean;
  isRequired?: boolean;
  label: string;
  options: string[];
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
};

export function EntityProfileContactSelectField(props: EntityProfileContactSelectFieldProps) {
  return (
    <FormControl isInvalid={props.isInvalid} isRequired={props.isRequired}>
      <FormLabel fontSize="13px" fontWeight={700}>
        {props.label}
      </FormLabel>
      <Select
        isDisabled={props.isDisabled}
        value={props.value}
        onChange={(event) => props.onChange(event.target.value)}
      >
        <option value="">{props.placeholder}</option>
        {props.options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </Select>
      {props.errorMessage ? <FormErrorMessage>{props.errorMessage}</FormErrorMessage> : null}
    </FormControl>
  );
}

type EntityProfileContactBooleanFieldProps = {
  falseLabel?: string;
  isDisabled: boolean;
  isRequired?: boolean;
  label: string;
  trueLabel?: string;
  value: boolean;
  onChange: (value: boolean) => void;
};

export function EntityProfileContactBooleanField(props: EntityProfileContactBooleanFieldProps) {
  return (
    <FormControl isRequired={props.isRequired}>
      <FormLabel fontSize="13px" fontWeight={700}>
        {props.label}
      </FormLabel>
      <RadioGroup
        value={props.value ? "true" : "false"}
        onChange={(value) => props.onChange(value === "true")}
      >
        <HStack minH="40px" spacing={4}>
          <Radio isDisabled={props.isDisabled} value="true">
            {props.trueLabel ?? "Yes"}
          </Radio>
          <Radio isDisabled={props.isDisabled} value="false">
            {props.falseLabel ?? "No"}
          </Radio>
        </HStack>
      </RadioGroup>
    </FormControl>
  );
}
